// src/lib/realtime/startRealtimeSession.ts
export async function startRealtimeSession({
  getAuthToken,
  onRemoteStream,
  onDataMessage,
  model = "gpt-realtime-mini-2025-10-06",
  voice = "echo",
}: {
  getAuthToken: () => Promise<string>;
  onRemoteStream: (stream: MediaStream) => void;
  onDataMessage?: (msg: any) => void;
  model?: string;
  voice?: string;
}) {
  console.log('[startRealtimeSession] Initiating realtime session');

  try {
    // Get Supabase JWT token
    const auth = await getAuthToken();
    console.log('[startRealtimeSession] Got auth token');

    // Call Supabase Edge Function to mint ephemeral token
    const { supabase } = await import('@/integrations/supabase/client');
    const { data, error } = await supabase.functions.invoke('realtime-session', {
      body: { model, voice },
      headers: { Authorization: `Bearer ${auth}` },
    });

    if (error) throw new Error(`realtime-session error: ${error.message}`);
    const { token, url, riskFlag, guidance } = data || {};
    if (!token || !url) throw new Error('realtime-session did not return token/url');
    console.log('[startRealtimeSession] Got ephemeral token');

    // Create WebRTC peer connection
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    pc.onconnectionstatechange = () => {
      console.log('[startRealtimeSession] pc.state =', pc.connectionState);
    };

    // Handle remote audio track
    pc.ontrack = (evt) => {
      console.log('[startRealtimeSession] Received remote audio track');
      onRemoteStream(evt.streams[0]);
    };

    // Track data channel and session update state
    let dcRef: RTCDataChannel | null = null;
    let sentUpdate = false;

    // Wire up data channel handlers
    const wireDC = (chan: RTCDataChannel) => {
      console.log('[startRealtimeSession] Wiring data channel:', chan.label);
      
      chan.onopen = () => {
        console.log('[startRealtimeSession] Data channel opened:', chan.label);
        
        // Send session.update once after channel opens
        if (!sentUpdate) {
          sentUpdate = true;
          const sessionUpdate = {
            type: "session.update",
            session: {
              modalities: ["text", "audio"],
              voice,
              turn_detection: {
                type: "server_vad",
                threshold: 0.5,
                prefix_padding_ms: 300,
                silence_duration_ms: 900,
                create_response: true,
                interrupt_response: true,
              },
              input_audio_transcription: {
                model: "whisper-1",
              },
              max_response_output_tokens: "inf",
            },
          };
          
          try {
            chan.send(JSON.stringify(sessionUpdate));
            console.log('[startRealtimeSession] Sent session.update');
          } catch (e) {
            console.warn('[startRealtimeSession] Failed to send session.update:', e);
          }
        }
      };

      chan.onclose = () => {
        console.log('[startRealtimeSession] Data channel closed:', chan.label);
      };

      chan.onerror = (e) => {
        console.warn('[startRealtimeSession] Data channel error:', chan.label, e);
      };

      chan.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          console.log('[startRealtimeSession] dc message type:', msg?.type);
          onDataMessage?.(msg);
        } catch (err) {
          console.warn('[startRealtimeSession] Failed to parse data channel message:', err);
        }
      };
    };

    // Listen for server-created data channels (preferred)
    pc.ondatachannel = (e) => {
      console.log('[startRealtimeSession] Remote data channel received:', e.channel.label);
      dcRef = e.channel;
      wireDC(dcRef);
    };

    // Create local data channel as fallback
    const localDC = pc.createDataChannel("oai-events");
    wireDC(localDC);

    // Get microphone with speech-friendly constraints
    const mic = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 24000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    console.log('[startRealtimeSession] Got microphone access');

    // Add microphone track
    mic.getTracks().forEach((track) => pc.addTrack(track, mic));

    // Create SDP offer
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    console.log('[startRealtimeSession] Created SDP offer');

    // Send SDP offer via edge function proxy (to avoid CORS)
    console.log('[startRealtimeSession] Sending SDP via edge function proxy');
    const { data: sdpData, error: sdpError } = await supabase.functions.invoke('realtime-session', {
      body: {
        action: 'sdp_exchange',
        model,
        voice,
        sdp: offer.sdp,
        ephemeral_token: token, // Pass ephemeral token to use same session
      },
      headers: { Authorization: `Bearer ${auth}` },
    });

    if (sdpError) {
      throw new Error(`SDP exchange failed: ${sdpError.message}`);
    }

    const answerSdp = sdpData;
    const answer = { type: "answer" as RTCSdpType, sdp: answerSdp };
    await pc.setRemoteDescription(answer);
    console.log('[startRealtimeSession] WebRTC connection established');

    // Return control + any metadata from EF (risk/guidance)
    return {
      stop: () => {
        console.log('[startRealtimeSession] Stopping session');
        try {
          mic.getTracks().forEach((track) => track.stop());
          pc.close();
        } catch (error) {
          console.warn('[startRealtimeSession] Error during cleanup:', error);
        }
      },
      sendJSON: (obj: any) => {
        try {
          const activeChannel = dcRef || localDC;
          if (activeChannel?.readyState === "open") {
            activeChannel.send(JSON.stringify(obj));
            console.debug('[startRealtimeSession] Sent dc message:', obj?.type || Object.keys(obj));
          } else {
            console.warn('[startRealtimeSession] Data channel not ready, message dropped:', obj?.type);
          }
        } catch (e) {
          console.warn('[startRealtimeSession] sendJSON error:', e);
        }
      },
      peer: pc,
      dataChannel: dcRef || localDC,
      microphone: mic,
      riskFlag: riskFlag ?? null,
      guidance: guidance ?? null,
    };
  } catch (error) {
    console.error('[startRealtimeSession] Failed to start session:', error);
    throw error;
  }
}
